// Command Center — super_admin unified dashboard.
// Dark editor's-desk aesthetic. Tabs: Overview · Sales · Quiz · Leads · Tasks · Services · Settings · Clients
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity, BarChart3, BookOpen, Briefcase, Bell, Check, Clock, ClipboardList,
  Copy, CreditCard, DollarSign, Download, ExternalLink, Eye, FileText, Filter,
  Heart, Inbox, Key, LayoutDashboard, Layers, LogOut, Mail, Menu, MessageSquare,
  Plus, RefreshCw, Search, Settings, ShoppingBag, Sparkles, Tag, Users,
  X, Trash2, Send, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import { usePortalAuth, PORTAL_API as API_PORTAL, formatApiError } from "./PortalAuth";

const API_ROOT = process.env.REACT_APP_BACKEND_URL;
const ADMIN = `${API_ROOT}/api/portal/admin`;

const TABS = [
  { key: "overview",  label: "Overview",  icon: LayoutDashboard },
  { key: "sales",     label: "Sales",     icon: DollarSign, financial: true },
  { key: "quiz",      label: "Quiz",      icon: Sparkles },
  { key: "leads",     label: "Leads",     icon: Inbox },
  { key: "tasks",     label: "Tasks",     icon: ClipboardList },
  { key: "clients",   label: "Clients",   icon: Users },
  { key: "services",  label: "Services",  icon: Tag },
  { key: "settings",  label: "Settings",  icon: Settings, financial: true },
];

// Only super_admin can see financial tabs (Sales, Settings/Stripe). Team members get the
// editorial-only subset.
const isSuper = (u) => u?.role === "super_admin";
const VISIBLE_TABS = (u) => isSuper(u) ? TABS : TABS.filter(t => !t.financial);

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
         " · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

export default function CommandCenter({ user }) {
  const { logout } = usePortalAuth();
  const [tab, setTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const openTab = (k) => { setTab(k); setSidebarOpen(false); };

  return (
    <div className="min-h-screen bg-jet text-cream lg:flex" data-testid="command-center">
      {/* MOBILE TOP BAR */}
      <div className="lg:hidden sticky top-0 z-30 bg-jet/95 backdrop-blur border-b border-cream/10 flex items-center justify-between px-4 py-3">
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 text-cream/80 hover:text-cream"
          data-testid="cc-mobile-menu"
          aria-label="Open menu"
        >
          <Menu size={20} strokeWidth={1.5}/>
        </button>
        <div className="flex items-center gap-2">
          <img src="/brand/mmedia-logo-light.png" alt="M Media" className="h-7 w-auto" draggable="false"/>
          <span className="overline text-cream/55">Command</span>
        </div>
        <button
          type="button"
          onClick={logout}
          className="p-2 -mr-2 text-cream/60 hover:text-cream"
          aria-label="Log out"
          data-testid="cc-mobile-logout"
        >
          <LogOut size={16} strokeWidth={1.5}/>
        </button>
      </div>

      {/* MOBILE OVERLAY */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* SIDEBAR */}
      <aside
        className={`fixed lg:sticky inset-y-0 left-0 z-50 lg:z-auto w-64 shrink-0 bg-jet border-r border-cream/10 flex flex-col h-screen lg:top-0 transform transition-transform duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="p-5 border-b border-cream/10 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/brand/mmedia-logo-light.png" alt="M Media" className="h-9 w-auto" draggable="false" />
            <div>
              <p className="overline text-cream/55">Command</p>
              <p className="font-display text-sm tracking-wider text-cream">Center</p>
            </div>
          </Link>
          <button
            type="button"
            className="lg:hidden p-1.5 text-cream/60 hover:text-cream"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <X size={16} strokeWidth={1.5}/>
          </button>
        </div>

        <nav className="px-3 py-4 flex-1 overflow-y-auto">
          {VISIBLE_TABS(user).map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`cc-tab-${t.key}`}
                onClick={() => openTab(t.key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 mb-1 text-sm transition-colors ${
                  active
                    ? "bg-cream text-jet font-medium"
                    : "text-cream/70 hover:bg-cream/5 hover:text-cream"
                }`}
              >
                <Icon size={15} strokeWidth={1.5}/>
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-cream/10">
          <div className="mb-3 px-2">
            <p className="overline text-cream/45">Signed in</p>
            <p className="text-sm text-cream truncate">{user.email}</p>
            <p className="text-[10px] tracking-[0.2em] uppercase text-gold mt-1">{user.role}</p>
          </div>
          <button
            type="button"
            data-testid="cc-logout"
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-cream/60 hover:text-cream hover:bg-cream/5 transition-colors"
          >
            <LogOut size={13} strokeWidth={1.5}/>
            Log out
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 min-w-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
          >
            {tab === "overview" && <OverviewTab user={user} />}
            {tab === "sales"    && isSuper(user) && <SalesTab />}
            {tab === "quiz"     && <QuizTab />}
            {tab === "leads"    && <LeadsTab />}
            {tab === "tasks"    && <TasksTab />}
            {tab === "clients"  && <ClientsTab />}
            {tab === "services" && <ServicesTab />}
            {tab === "settings" && isSuper(user) && <SettingsTab />}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// HEADER ROW (used in every tab)
// ─────────────────────────────────────────────────────────
function TabHeader({ title, subtitle, children }) {
  return (
    <div className="border-b border-cream/10 px-5 sm:px-8 lg:px-12 py-5 sm:py-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
      <div className="min-w-0">
        <p className="overline text-cream/45 mb-1">Command Center</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl text-cream">{title}</h1>
        {subtitle && <p className="text-cream/55 mt-2 text-sm">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// OVERVIEW
// ═════════════════════════════════════════════════════════
function OverviewTab({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const showMoney = isSuper(user);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${ADMIN}/overview`, { withCredentials: true });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading || !data) {
    return <div className="p-12 text-cream/50 italic">Loading…</div>;
  }

  return (
    <>
      <TabHeader title="The Desk" subtitle="Everything that happened, in one frame.">
        <button type="button" onClick={reload} className="px-3 py-2 text-xs tracking-[0.2em] uppercase border border-cream/15 hover:bg-cream/5">
          <RefreshCw size={12} className="inline mr-1.5"/> Refresh
        </button>
      </TabHeader>

      <div className="p-5 sm:p-8 lg:p-12 space-y-8 sm:space-y-10">
        {/* KPI ROW */}
        <div className={`grid gap-3 sm:gap-4 ${showMoney ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-2 lg:grid-cols-2"}`}>
          {showMoney && <KPI icon={DollarSign} label="Revenue · all-time" value={money(data.revenue.total_usd)} sub={`${data.revenue.paid_orders} paid orders`}/>}
          {showMoney && <KPI icon={Activity} label="Revenue · 30d" value={money(data.revenue.last_30_days)} sub={`${money(data.revenue.last_7_days)} in last 7`}/>}
          <KPI icon={Inbox} label="Open leads" value={data.leads.new} sub={`${data.leads.total} total applications`}/>
          <KPI icon={Download} label="Free downloads" value={data.free_downloads} sub={`${data.quiz_submissions} quiz responses`}/>
        </div>

        {/* CHART + TOP PRODUCTS (super-admin only — financial visibility) */}
        {showMoney && (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-cream/5 border border-cream/10 p-5 sm:p-6">
            <div className="flex items-center justify-between mb-6 gap-3">
              <div className="min-w-0">
                <p className="overline text-cream/45 mb-1">Revenue · last 14 days</p>
                <p className="font-display text-xl sm:text-2xl text-cream truncate">{money(data.revenue.last_30_days)} this month</p>
              </div>
              <BarChart3 size={18} className="text-cream/40 shrink-0"/>
            </div>
            <RevenueChart data={data.revenue.chart}/>
          </div>

          <div className="bg-cream/5 border border-cream/10 p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="overline text-cream/45">Top movers</p>
              <ShoppingBag size={14} className="text-cream/40"/>
            </div>
            <ul className="space-y-3">
              {data.top_products.length === 0 && (
                <li className="text-sm text-cream/40 italic">No orders yet.</li>
              )}
              {data.top_products.map((p, i) => (
                <li key={p.slug} className="flex items-start gap-3 text-sm">
                  <span className="font-display text-lg text-cream/40 w-6">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-cream truncate">{p.title}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-cream/40">{p.tier || "—"}</p>
                  </div>
                  <span className="text-cream/60 text-sm">{p.count}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        )}

        {/* ACTIVITY FEED */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl sm:text-2xl text-cream">Activity</h2>
            <p className="text-[10px] tracking-[0.2em] uppercase text-cream/40">Latest 14 events</p>
          </div>
          <div className="bg-cream/5 border border-cream/10 divide-y divide-cream/10">
            {data.activity.length === 0 && (
              <p className="p-6 text-sm text-cream/40 italic">No activity yet.</p>
            )}
            {data.activity.map((a, i) => (
              <ActivityRow key={i} a={a} showMoney={showMoney}/>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function KPI({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-cream/5 border border-cream/10 p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="overline text-cream/45">{label}</p>
        <Icon size={14} className="text-cream/40"/>
      </div>
      <p className="font-display text-3xl text-cream leading-none">{value}</p>
      {sub && <p className="text-[11px] text-cream/45 mt-2">{sub}</p>}
    </div>
  );
}

function ActivityRow({ a, showMoney = true }) {
  const map = {
    purchase:      { icon: CreditCard, label: "Purchase",      tint: "text-gold" },
    free_download: { icon: Download,   label: "Free download", tint: "text-cream/60" },
    lead:          { icon: Mail,       label: "Application",   tint: "text-cream/60" },
  };
  const meta = map[a.kind] || { icon: Activity, label: a.kind, tint: "text-cream/60" };
  const Icon = meta.icon;
  return (
    <div className="px-4 sm:px-5 py-3 flex items-center gap-3 sm:gap-4">
      <Icon size={14} className={`${meta.tint} shrink-0`}/>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-cream truncate">
          <span className="text-cream/45">{meta.label} · </span>
          {a.title || "—"}
        </p>
        <p className="text-[11px] text-cream/40 truncate">{a.email || "—"}</p>
      </div>
      {showMoney && a.amount != null && (
        <span className="text-sm text-cream font-display tabular-nums hidden sm:inline">{money(a.amount)}</span>
      )}
      <span className="text-[10px] text-cream/40 w-20 sm:w-32 text-right tabular-nums shrink-0">{fmtDate(a.created_at)}</span>
    </div>
  );
}

function RevenueChart({ data }) {
  const max = Math.max(...data.map(d => d.revenue), 1);
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map((d) => {
        const h = Math.max((d.revenue / max) * 100, 2);
        return (
          <div key={d.date} className="flex-1 group relative">
            <div
              className="bg-cream/30 group-hover:bg-gold transition-colors mx-auto"
              style={{ height: `${h}%`, minHeight: 2, width: "70%" }}
            />
            <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[9px] text-cream opacity-0 group-hover:opacity-100 whitespace-nowrap">
              {money(d.revenue)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// SALES
// ═════════════════════════════════════════════════════════
function SalesTab() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${ADMIN}/orders?kind=${filter}`, { withCredentials: true });
      setOrders(data.orders);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [filter]);

  useEffect(() => { reload(); }, [reload]);

  const resend = async (tx) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${ADMIN}/orders/${tx.session_id || tx.id}/resend-link`, {}, { withCredentials: true });
      const url = `${API_ROOT}${data.download_url}`;
      await navigator.clipboard.writeText(url);
      toast.success("Fresh download link copied to clipboard.");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const totalPaid = useMemo(() =>
    orders.filter(o => o.kind === "paid" && o.payment_status === "paid").reduce((a, o) => a + Number(o.amount || 0), 0),
    [orders]
  );

  return (
    <>
      <TabHeader
        title="Sales"
        subtitle={`${orders.length} orders · ${money(totalPaid)} captured`}
      >
        <div className="flex border border-cream/15">
          {[["all", "All"], ["paid", "Paid"], ["free", "Free"]].map(([k, l]) => (
            <button
              key={k}
              type="button"
              data-testid={`sales-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-3 py-2 text-xs tracking-[0.2em] uppercase ${filter === k ? "bg-cream text-jet" : "text-cream/60 hover:text-cream"}`}
            >
              {l}
            </button>
          ))}
        </div>
        <button type="button" onClick={reload} className="px-3 py-2 text-xs tracking-[0.2em] uppercase border border-cream/15 hover:bg-cream/5">
          <RefreshCw size={12} className="inline mr-1"/>Refresh
        </button>
      </TabHeader>

      <div className="p-4 sm:p-6 lg:p-10">
        <div className="bg-cream/5 border border-cream/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-[0.25em] uppercase text-cream/50 border-b border-cream/10">
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Kind</th>
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-left px-4 py-3">Buyer</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-cream/40 italic text-center">No orders yet.</td></tr>
              )}
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-cream/5 hover:bg-cream/5">
                  <td className="px-4 py-3 text-cream/60 text-[11px] tabular-nums">{fmtDate(o.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 ${o.kind === "paid" ? "bg-gold/20 text-gold" : "bg-cream/10 text-cream/60"}`}>
                      {o.kind}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-cream truncate max-w-[280px]">{o.title || o.slug}</td>
                  <td className="px-4 py-3 text-cream/70">
                    <div className="truncate max-w-[200px]">{o.name || "—"}</div>
                    <div className="text-[10px] text-cream/40 truncate">{o.email || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-right text-cream tabular-nums">{money(o.amount)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.18em] ${o.payment_status === "paid" || o.status === "delivered" ? "text-gold" : "text-cream/40"}`}>
                      {o.payment_status || o.status || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {o.kind === "paid" && o.payment_status === "paid" && (
                      <button
                        type="button"
                        disabled={busy}
                        data-testid={`resend-${o.id}`}
                        onClick={() => resend(o)}
                        className="text-[10px] tracking-[0.2em] uppercase text-gold hover:text-cream"
                      >
                        <Send size={11} className="inline mr-1"/>Re-send link
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ═════════════════════════════════════════════════════════
// QUIZ
// ═════════════════════════════════════════════════════════
function QuizTab() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${ADMIN}/quiz`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!data) return <div className="p-12 text-cream/50 italic">Loading…</div>;

  const maxCat = Math.max(...data.category_breakdown.map(c => c.count), 1);

  return (
    <>
      <TabHeader title="Quiz · Demand Heatmap" subtitle={`${data.total} responses · what people are actually asking for.`}/>
      <div className="p-4 sm:p-8 lg:p-12 space-y-8 sm:space-y-10">
        {/* Category heatmap */}
        <div className="bg-cream/5 border border-cream/10 p-5 sm:p-6">
          <p className="overline text-cream/45 mb-6">Where the appetite is</p>
          <div className="space-y-3">
            {data.category_breakdown.map((c) => (
              <div key={c.key} className="flex items-center gap-3 sm:gap-4">
                <span className="text-xs sm:text-sm text-cream w-28 sm:w-44 truncate">{c.label}</span>
                <div className="flex-1 h-7 bg-cream/5 relative">
                  <div
                    className="absolute inset-y-0 left-0 bg-gold/40"
                    style={{ width: `${(c.count / maxCat) * 100}%` }}
                  />
                </div>
                <span className="text-cream font-display tabular-nums w-12 text-right">{c.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top picks */}
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="bg-cream/5 border border-cream/10 p-6">
            <p className="overline text-cream/45 mb-4">Products quiz recommends most</p>
            <ul className="space-y-2">
              {data.top_picks.length === 0 && (
                <li className="text-sm text-cream/40 italic">No quiz takers yet.</li>
              )}
              {data.top_picks.map((p, i) => (
                <li key={p.slug} className="flex items-center gap-3 text-sm">
                  <span className="font-display text-lg text-cream/40 w-5">{i + 1}</span>
                  <span className="flex-1 text-cream truncate">{p.title}</span>
                  <span className="text-cream/60 tabular-nums">{p.count}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Submissions */}
          <div className="bg-cream/5 border border-cream/10 p-6 max-h-[520px] overflow-y-auto">
            <p className="overline text-cream/45 mb-4">Most recent responses</p>
            <ul className="divide-y divide-cream/10">
              {data.submissions.slice(0, 30).map((s) => (
                <li key={s.id} className="py-3 text-sm">
                  <div className="flex justify-between text-cream/70">
                    <span className="font-display">{s.top_category}</span>
                    <span className="text-[10px] text-cream/40 tabular-nums">{fmtDate(s.created_at)}</span>
                  </div>
                  <div className="text-[11px] text-cream/50 mt-1">
                    {Object.entries(s.answers || {}).map(([k, v]) => `${k}:${v}`).join(" · ")}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}

// ═════════════════════════════════════════════════════════
// LEADS
// ═════════════════════════════════════════════════════════
function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("new");
  const [selected, setSelected] = useState(null);

  const reload = async () => {
    try {
      const { data } = await axios.get(`${ADMIN}/leads`, { withCredentials: true });
      setLeads(data.leads);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { reload(); }, []);

  const updateStatus = async (lead, status) => {
    try {
      await axios.patch(`${ADMIN}/leads/${lead.id}`, { status }, { withCredentials: true });
      toast.success(`Marked ${status}`);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return leads;
    return leads.filter(l => l.status === filter);
  }, [leads, filter]);

  return (
    <>
      <TabHeader title="Leads · Applications" subtitle={`${leads.length} total · ${leads.filter(l => l.status === "new").length} new`}>
        <div className="flex border border-cream/15">
          {[["new", "New"], ["read", "Read"], ["archived", "Archived"], ["all", "All"]].map(([k, l]) => (
            <button
              key={k}
              type="button"
              data-testid={`leads-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-3 py-2 text-xs tracking-[0.2em] uppercase ${filter === k ? "bg-cream text-jet" : "text-cream/60 hover:text-cream"}`}
            >
              {l}
            </button>
          ))}
        </div>
      </TabHeader>

      <div className="p-4 sm:p-6 lg:p-10 grid lg:grid-cols-12 gap-4 sm:gap-6">
        {/* List */}
        <ul className="lg:col-span-5 bg-cream/5 border border-cream/10 divide-y divide-cream/10 max-h-[640px] overflow-y-auto">
          {filtered.length === 0 && (
            <li className="p-6 text-sm text-cream/40 italic">Nothing here.</li>
          )}
          {filtered.map(l => (
            <li
              key={l.id}
              onClick={() => setSelected(l)}
              data-testid={`lead-${l.id}`}
              className={`p-4 cursor-pointer hover:bg-cream/5 ${selected?.id === l.id ? "bg-cream/10" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-cream truncate">{l.name}</p>
                  <p className="text-[11px] text-cream/45 truncate">{l.email}</p>
                </div>
                <span className={`text-[9px] tracking-[0.2em] uppercase shrink-0 px-1.5 py-0.5 ${
                  l.status === "new" ? "bg-gold/20 text-gold" :
                  l.status === "read" ? "bg-cream/10 text-cream/60" : "bg-cream/5 text-cream/30"
                }`}>{l.status}</span>
              </div>
              <p className="text-[10px] text-cream/40 mt-2">{l.title || l.company || "—"} · {fmtDate(l.created_at)}</p>
            </li>
          ))}
        </ul>

        {/* Detail */}
        <div className="lg:col-span-7 bg-cream/5 border border-cream/10 p-6 min-h-[400px]">
          {!selected ? (
            <p className="text-sm text-cream/40 italic text-center pt-20">Select an application.</p>
          ) : (
            <>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="font-display text-2xl text-cream">{selected.name}</h3>
                  <p className="text-sm text-cream/55">{selected.email}{selected.phone ? ` · ${selected.phone}` : ""}</p>
                  <p className="text-[11px] text-cream/40 mt-1">{fmtDate(selected.created_at)}</p>
                </div>
                <div className="flex gap-2">
                  <button type="button" data-testid="lead-mark-read" onClick={() => updateStatus(selected, "read")} className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase border border-cream/15 hover:bg-cream/10">Read</button>
                  <button type="button" data-testid="lead-mark-archived" onClick={() => updateStatus(selected, "archived")} className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase border border-cream/15 hover:bg-cream/10">Archive</button>
                </div>
              </div>
              <Field label="Title / Brand">{selected.title}{selected.company ? ` · ${selected.company}` : ""}</Field>
              <Field label="Website">{selected.website && <a href={selected.website} target="_blank" rel="noreferrer" className="text-gold hover:underline">{selected.website}</a>}</Field>
              <Field label="Stage / Revenue">{[selected.stage, selected.revenue].filter(Boolean).join(" · ")}</Field>
              <Field label="Achievements">{selected.achievements}</Field>
              <Field label="Why us">{selected.why_us}</Field>
              <Field label="Message">{selected.message}</Field>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function Field({ label, children }) {
  if (!children) return null;
  return (
    <div className="mb-4">
      <p className="overline text-cream/45 mb-1">{label}</p>
      <div className="text-sm text-cream/85 whitespace-pre-wrap break-words">{children}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// TASKS · KANBAN ACROSS ALL CLIENTS
// ═════════════════════════════════════════════════════════
function TasksTab() {
  const [tasks, setTasks] = useState([]);
  const reload = async () => {
    try {
      const { data } = await axios.get(`${ADMIN}/tasks`, { withCredentials: true });
      setTasks(data.tasks);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { reload(); }, []);

  const columns = useMemo(() => {
    const byStatus = { todo: [], in_progress: [], blocked: [], done: [] };
    tasks.forEach(t => {
      const s = (t.status || "todo").toLowerCase();
      (byStatus[s] || byStatus.todo).push(t);
    });
    return byStatus;
  }, [tasks]);

  const setStatus = async (task, status) => {
    try {
      await axios.patch(`${API_PORTAL}/tasks/${task.id}`, { status }, { withCredentials: true });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const COLS = [
    { key: "todo",        label: "To-do",        tint: "border-cream/20" },
    { key: "in_progress", label: "In progress",  tint: "border-gold" },
    { key: "blocked",     label: "Blocked",      tint: "border-red-400/60" },
    { key: "done",        label: "Done",         tint: "border-green-500/40" },
  ];

  return (
    <>
      <TabHeader title="Tasks" subtitle={`${tasks.length} tasks across every client`}/>
      <div className="p-4 sm:p-6 lg:p-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {COLS.map(c => (
            <div key={c.key} className={`bg-cream/5 border-t-2 ${c.tint} p-4 min-h-[400px]`}>
              <div className="flex items-center justify-between mb-4">
                <p className="overline text-cream/55">{c.label}</p>
                <span className="text-[11px] text-cream/40 tabular-nums">{columns[c.key].length}</span>
              </div>
              <div className="space-y-2">
                {columns[c.key].map(t => (
                  <div key={t.id} className="bg-jet border border-cream/10 p-3 group">
                    <p className="text-sm text-cream leading-snug">{t.title}</p>
                    {t.client_name && <p className="text-[10px] text-cream/40 mt-1.5 tracking-[0.18em] uppercase">{t.client_name}</p>}
                    <div className="mt-3 flex gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
                      {COLS.filter(x => x.key !== c.key).map(x => (
                        <button
                          key={x.key}
                          type="button"
                          onClick={() => setStatus(t, x.key)}
                          className="text-[9px] tracking-[0.15em] uppercase text-cream/50 hover:text-cream px-1.5 py-0.5 border border-cream/10 hover:border-cream/30"
                        >
                          → {x.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {columns[c.key].length === 0 && (
                  <p className="text-[11px] text-cream/30 italic">Empty.</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ═════════════════════════════════════════════════════════
// CLIENTS — per-client file requests + service toggles
// ═════════════════════════════════════════════════════════
function ClientsTab() {
  const [clients, setClients] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [products, setProducts] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [fileRequests, setFileRequests] = useState([]);
  const [showCreateFR, setShowCreateFR] = useState(false);

  const reloadClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_PORTAL}/clients`, { withCredentials: true });
      setClients(data);
      if (!selectedId && data.length) setSelectedId(data[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [selectedId]);

  const reloadServices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${ADMIN}/services`, { withCredentials: true });
      setProducts(data.products);
      setOverrides(data.overrides);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const reloadFR = useCallback(async (clientId) => {
    if (!clientId) return setFileRequests([]);
    try {
      const { data } = await axios.get(`${ADMIN}/file-requests?client_id=${clientId}`, { withCredentials: true });
      setFileRequests(data.file_requests);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { reloadClients(); reloadServices(); }, [reloadClients, reloadServices]);
  useEffect(() => { reloadFR(selectedId); }, [selectedId, reloadFR]);

  const selected = clients.find(c => c.id === selectedId);
  const overridesForSel = (selected && overrides[selected.id]) || {};

  // Default visibility: free + intensives + signature. Use override when set.
  const isServiceEnabled = (slug) => {
    if (selected && slug in (overrides[selected.id] || {})) return overrides[selected.id][slug];
    const p = products.find(x => x.slug === slug);
    if (!p) return false;
    return p.is_free || ["Intensive", "Signature", "Private Council"].includes(p.tier);
  };

  const toggleService = async (slug) => {
    if (!selected) return;
    const next = !isServiceEnabled(slug);
    try {
      await axios.post(`${ADMIN}/services/toggle`, { client_id: selected.id, slug, enabled: next }, { withCredentials: true });
      setOverrides(prev => ({
        ...prev,
        [selected.id]: { ...(prev[selected.id] || {}), [slug]: next },
      }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const enabledCount = useMemo(() =>
    products.filter(p => isServiceEnabled(p.slug)).length,
    [products, overrides, selected]
  );

  return (
    <>
      <TabHeader title="Clients" subtitle={`${clients.length} active · file requests · per-client service toggles`}/>
      <div className="grid lg:grid-cols-12 gap-0 min-h-[calc(100vh-120px)]">
        {/* Client list */}
        <aside className="lg:col-span-3 border-r border-cream/10 overflow-y-auto">
          {clients.length === 0 && (
            <p className="p-6 text-cream/40 italic text-sm">No clients yet.</p>
          )}
          {clients.map(c => (
            <button
              key={c.id}
              type="button"
              data-testid={`client-row-${c.id}`}
              onClick={() => setSelectedId(c.id)}
              className={`w-full text-left p-4 border-b border-cream/5 flex items-center gap-3 hover:bg-cream/5 transition-colors ${
                selectedId === c.id ? "bg-cream/10" : ""
              }`}
            >
              <div className="w-10 h-10 bg-cream/10 grid place-items-center text-cream/70 font-display text-sm shrink-0">
                {(c.display_name || "?").split(" ").map(w => w[0]).slice(0,2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-cream text-sm truncate">{c.display_name || "Unnamed"}</p>
                <p className="text-[11px] text-cream/40 truncate">{c.user?.email || "—"}</p>
              </div>
            </button>
          ))}
        </aside>

        {/* Detail */}
        <section className="lg:col-span-9 p-4 sm:p-6 lg:p-10 overflow-y-auto">
          {!selected ? (
            <p className="text-cream/40 italic">Select a client.</p>
          ) : (
            <>
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6 mb-8 sm:mb-10 pb-6 border-b border-cream/10">
                <div className="min-w-0">
                  <p className="overline text-cream/45 mb-1">Client file</p>
                  <h2 className="font-display text-4xl text-cream truncate">{selected.display_name || "Unnamed"}</h2>
                  <p className="text-cream/55 text-sm mt-2">
                    {selected.user?.email}{selected.tagline ? ` · ${selected.tagline}` : ""}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    data-testid="create-file-request"
                    onClick={() => setShowCreateFR(true)}
                    className="px-4 py-2 bg-cream text-jet text-xs tracking-[0.25em] uppercase hover:bg-cream/90 flex items-center gap-2"
                  >
                    <Plus size={12} strokeWidth={2}/> Request from client
                  </button>
                </div>
              </div>

              {/* File requests */}
              <div className="mb-12">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-2xl text-cream">File Requests</h3>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-cream/40">{fileRequests.length} total</p>
                </div>
                <div className="bg-cream/5 border border-cream/10 divide-y divide-cream/10">
                  {fileRequests.length === 0 && (
                    <p className="p-6 text-sm text-cream/40 italic">No requests yet. Send one to start the file exchange.</p>
                  )}
                  {fileRequests.map(fr => (
                    <FileRequestRow key={fr.id} fr={fr} onChange={() => reloadFR(selected.id)} />
                  ))}
                </div>
              </div>

              {/* Service toggles */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-2xl text-cream">Service Showcase</h3>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-cream/40">{enabledCount} of {products.length} visible</p>
                </div>
                <p className="text-sm text-cream/55 max-w-2xl mb-5">
                  Toggle which Studio editions this client can see surfaced inside their portal. Free items and intensives are on by default.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {products.map(p => {
                    const enabled = isServiceEnabled(p.slug);
                    return (
                      <button
                        key={p.slug}
                        type="button"
                        data-testid={`service-toggle-${p.slug}`}
                        onClick={() => toggleService(p.slug)}
                        className={`text-left flex gap-3 p-3 border transition-colors ${
                          enabled
                            ? "border-gold/60 bg-gold/10 hover:bg-gold/15"
                            : "border-cream/10 bg-cream/5 hover:border-cream/30 opacity-60 hover:opacity-100"
                        }`}
                      >
                        <img
                          src={`${API_ROOT}${p.cover_url}`}
                          alt=""
                          onError={(e) => { e.target.style.display = "none"; }}
                          className="w-12 h-16 object-cover bg-cream/10 shrink-0 grayscale"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-[9px] tracking-[0.2em] uppercase text-cream/40">No. {p.edition_no} · {p.tier}</p>
                          <p className="text-cream text-sm leading-tight mt-1 truncate">{p.title}</p>
                          <p className="text-[10px] text-cream/40 mt-1">{p.is_free ? "Free" : money(p.price)}</p>
                        </div>
                        <div className={`w-4 h-4 rounded-full border-2 shrink-0 ${enabled ? "bg-gold border-gold" : "border-cream/30"}`}/>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      <FileRequestModal
        open={showCreateFR}
        onClose={() => setShowCreateFR(false)}
        client={selected}
        onCreated={() => { reloadFR(selected?.id); setShowCreateFR(false); }}
      />
    </>
  );
}

function FileRequestRow({ fr, onChange }) {
  const setStatus = async (status) => {
    try {
      await axios.patch(`${ADMIN}/file-requests/${fr.id}`, { status }, { withCredentials: true });
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="p-4 flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3 mb-1">
          <p className="text-cream text-sm">{fr.title}</p>
          <span className={`text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 ${
            fr.status === "open" ? "bg-gold/20 text-gold" :
            fr.status === "completed" ? "bg-green-500/20 text-green-400" :
            "bg-cream/5 text-cream/40"
          }`}>{fr.status}</span>
        </div>
        {fr.description && <p className="text-xs text-cream/55 mb-2 whitespace-pre-wrap">{fr.description}</p>}
        {fr.fields?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {fr.fields.map((f, i) => (
              <span key={i} className="text-[10px] text-cream/50 border border-cream/10 px-2 py-0.5">{f}</span>
            ))}
          </div>
        )}
        <p className="text-[10px] text-cream/40 mt-2">{fmtDate(fr.created_at)}</p>
      </div>
      <div className="flex gap-1 shrink-0">
        {fr.status === "open" && (
          <>
            <button type="button" onClick={() => setStatus("completed")} className="text-[10px] tracking-[0.18em] uppercase text-gold hover:text-cream border border-gold/30 hover:border-cream px-2 py-1">Complete</button>
            <button type="button" onClick={() => setStatus("cancelled")} className="text-[10px] tracking-[0.18em] uppercase text-cream/40 hover:text-cream border border-cream/10 hover:border-cream/30 px-2 py-1">Cancel</button>
          </>
        )}
        {fr.status !== "open" && (
          <button type="button" onClick={() => setStatus("open")} className="text-[10px] tracking-[0.18em] uppercase text-cream/40 hover:text-cream border border-cream/10 hover:border-cream/30 px-2 py-1">Reopen</button>
        )}
      </div>
    </div>
  );
}

function FileRequestModal({ open, onClose, client, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [fieldsText, setFieldsText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setTitle(""); setDescription(""); setFieldsText(""); }
  }, [open]);

  if (!open || !client) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fields = fieldsText.split("\n").map(s => s.trim()).filter(Boolean);
      await axios.post(`${ADMIN}/file-requests`, {
        client_id: client.id, title, description, fields,
      }, { withCredentials: true });
      toast.success("Request sent.");
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-jet/85 backdrop-blur grid place-items-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="bg-jet border border-cream/15 w-full max-w-lg p-8 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-cream/60 hover:text-cream"
            aria-label="Close"
          >
            <X size={18}/>
          </button>
          <p className="overline text-cream/45 mb-2">New file request</p>
          <h2 className="font-display text-3xl text-cream mb-2">Request from {client.display_name}</h2>
          <p className="text-sm text-cream/55 mb-6">They&apos;ll see this in their portal as a branded ask.</p>
          <form onSubmit={submit} className="space-y-3">
            <input
              required maxLength={200}
              data-testid="fr-title"
              placeholder="What are you asking for? e.g. Brand assets package"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-jet border border-cream/15 px-4 py-3 text-sm text-cream focus:outline-none focus:border-gold"
            />
            <textarea
              rows={3}
              data-testid="fr-description"
              placeholder="Context, instructions, anything they should know."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-jet border border-cream/15 px-4 py-3 text-sm text-cream focus:outline-none focus:border-gold resize-none"
            />
            <textarea
              rows={4}
              data-testid="fr-fields"
              placeholder="Specific items (one per line). e.g.&#10;Logo (.svg)&#10;Brand colors&#10;Founder bio"
              value={fieldsText}
              onChange={(e) => setFieldsText(e.target.value)}
              className="w-full bg-jet border border-cream/15 px-4 py-3 text-sm text-cream font-mono focus:outline-none focus:border-gold resize-none"
            />
            <button
              type="submit"
              disabled={busy || !title.trim()}
              data-testid="fr-submit"
              className="w-full bg-cream text-jet py-3 text-xs tracking-[0.25em] uppercase hover:bg-cream/90 disabled:opacity-60"
            >
              {busy ? "Sending…" : "Send request"}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ═════════════════════════════════════════════════════════
// SERVICES SHOWCASE
// ═════════════════════════════════════════════════════════
function ServicesTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${ADMIN}/services`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!data) return <div className="p-12 text-cream/50 italic">Loading…</div>;

  return (
    <>
      <TabHeader
        title="Services Showcase"
        subtitle={`${data.products.length} Studio products you can surface inside the portal.`}
      />
      <div className="p-4 sm:p-8 lg:p-12">
        <p className="text-sm text-cream/60 mb-6 max-w-2xl">
          These are the editions in your Studio. Mirror any of them into the client portal so clients can request access — phase 2 will let you flip them on/off per client.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.products.map(p => (
            <div key={p.slug} className="bg-cream/5 border border-cream/10 p-4 flex gap-3">
              <img
                src={`${API_ROOT}${p.cover_url}`}
                alt=""
                onError={(e) => { e.target.style.display = "none"; }}
                className="w-14 h-18 object-cover bg-cream/10 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="text-[9px] tracking-[0.2em] uppercase text-cream/40">No. {p.edition_no} · {p.tier}</p>
                <p className="text-cream text-sm leading-tight mt-1 truncate">{p.title}</p>
                <p className="text-[10px] text-cream/40 mt-1">{p.is_free ? "Free" : money(p.price)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ═════════════════════════════════════════════════════════
// SETTINGS
// ═════════════════════════════════════════════════════════
function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [stripeKey, setStripeKey] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const { data } = await axios.get(`${ADMIN}/settings`, { withCredentials: true });
      setSettings(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { reload(); }, []);

  const saveStripe = async () => {
    if (!stripeKey.trim()) { toast.error("Paste a Stripe key first."); return; }
    setBusy(true);
    try {
      await axios.post(`${ADMIN}/settings/stripe-key`, { api_key: stripeKey.trim() }, { withCredentials: true });
      toast.success("Stripe key updated.");
      setStripeKey("");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!settings) return <div className="p-12 text-cream/50 italic">Loading…</div>;

  return (
    <>
      <TabHeader title="Settings" subtitle="Live keys and brand preferences."/>
      <div className="p-4 sm:p-8 lg:p-12 max-w-3xl space-y-8">
        {/* Stripe */}
        <div className="bg-cream/5 border border-cream/10 p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="overline text-cream/45 mb-1">Stripe</p>
              <p className="font-display text-2xl text-cream">Payments</p>
            </div>
            <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${settings.stripe_mode === "live" ? "bg-gold/30 text-gold" : "bg-cream/10 text-cream/60"}`}>
              {settings.stripe_mode}
            </span>
          </div>
          <div className="bg-jet border border-cream/10 p-4 mb-4">
            <p className="text-xs text-cream/50 mb-1">Current key</p>
            <p className="font-mono text-sm text-cream">{settings.stripe_key_preview}</p>
            {settings.stripe_last_updated && (
              <p className="text-[10px] text-cream/40 mt-2">Last updated · {fmtDate(settings.stripe_last_updated)}</p>
            )}
          </div>
          <div className="space-y-3">
            <input
              type="password"
              data-testid="stripe-key-input"
              placeholder="sk_live_… (paste a new key to swap)"
              value={stripeKey}
              onChange={(e) => setStripeKey(e.target.value)}
              className="w-full bg-jet border border-cream/15 px-4 py-3 text-sm text-cream focus:outline-none focus:border-gold font-mono"
            />
            <button
              type="button"
              data-testid="stripe-key-save"
              disabled={busy || !stripeKey.trim()}
              onClick={saveStripe}
              className="px-6 py-3 bg-cream text-jet text-xs tracking-[0.25em] uppercase hover:bg-cream/90 disabled:opacity-50"
            >
              {busy ? "Saving…" : "Swap Stripe key"}
            </button>
            <p className="text-[11px] text-cream/40">
              Persists to backend/.env and reloads automatically. Use <span className="font-mono">sk_live_…</span> for production.
            </p>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-cream/5 border border-cream/10 p-6">
          <p className="overline text-cream/45 mb-3">Coming soon</p>
          <ul className="space-y-2 text-sm text-cream/60">
            <li>· Resend email key (wire post-Wix domain switch)</li>
            <li>· Brand colors / fonts override per portal</li>
            <li>· Webhook URLs (Zapier, Slack, etc.)</li>
          </ul>
        </div>
      </div>
    </>
  );
}
